import { FC, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Icon } from 'features/common/components/Icon';
import { M } from 'features/common/components/Icon/utils';
import { getBase64 } from 'utils/utils';
import { ModalDelete } from 'features/common/components/ModalDelete';
import { ImageType } from 'services/UploadImage/types';
import * as Styled from './CustomImageInput.styles';

interface ImagesType {
	ID: number;
	image: string;
	url?: string;
}

interface CustomImageInputProps {
	id?: string;
	name: string;
	maxPicturesAllowed: number;
	isPunchup?: boolean;
}

export const CustomImageInput: FC<CustomImageInputProps> = ({ id, name, maxPicturesAllowed, isPunchup = false }) => {
	const [showModal, setShowModal] = useState(false);
	const [selectedImage, setSelectedImage] = useState<ImagesType | ImageType>();
	const { t } = useTranslation();

	const {
		setValue,
		getValues,
		watch,
		formState: { errors },
	} = useFormContext();
	const error = errors[name];

	// this will cause immediate re-render after image is uploaded
	watch(id ?? name);

	const { getRootProps, getInputProps } = useDropzone({
		onDrop: async (acceptedFiles) => {
			const valuesToAdd = await Promise.all(
				acceptedFiles.map(async (file) => ({
					url: URL.createObjectURL(file),
					base64: await getBase64(file),
					name: file.name,
				}))
			);
			const valuesToSave = [...getValues(id ?? name), ...valuesToAdd];
			setValue(id ?? name, valuesToSave);
		},
		accept: {
			'image/jpeg': [],
			'image/png': [],
		},
		maxFiles: maxPicturesAllowed - getValues(id ?? name)?.length,
		disabled: getValues(id ?? name)?.length >= maxPicturesAllowed,
	});

	const isAnImageType = (obj: ImagesType | ImageType | undefined): obj is ImageType =>
		obj !== undefined && 'url' in obj && 'name' in obj && 'base64' in obj;

	const handleDeleteImage = () => {
		const values: ImagesType[] = getValues(id ?? name);
		const _values = values.filter((image) => {
			if (isAnImageType(selectedImage)) return image.url !== selectedImage.url;
			else return image.ID !== selectedImage?.ID;
		});
		setValue(id ?? name, _values);
		setShowModal(false);
	};

	const modalInfo = {
		modalTitle: t('imageInput-message'),
		modalMessage: t('imageInput-title'),
		onDelete: handleDeleteImage,
	};

	return (
		<Styled.Container>
			<ModalDelete showModal={showModal} closeModal={() => setShowModal(!showModal)} modalInfo={modalInfo} />
			<Styled.ImagesRow>
				{getValues(id ?? name)?.map((image: ImagesType, index: number) => (
					<Styled.ImagePreview
						key={`${image.image}-${index}`}
						src={image.image || image.url}
						onClick={() => {
							setShowModal(!showModal);
							setSelectedImage(image);
						}}
					/>
				))}

				{getValues(id ?? name)?.length < maxPicturesAllowed && (
					<Styled.DropzoneContainer {...getRootProps()} isPunchup={isPunchup}>
						<input {...getInputProps()} id={id ?? name} />
						<Styled.PlusIcon isPunchup={isPunchup}>
							<Icon iconName="plus" size={M} />
						</Styled.PlusIcon>
						<Styled.DropzoneText isPunchup={isPunchup}>
							{t('editEvent-images-dropFile')}
							<Styled.BrowseLink isPunchup={isPunchup}>{t('editEvent-images-browse')}</Styled.BrowseLink>
						</Styled.DropzoneText>
						<Styled.AddLink isPunchup={isPunchup}>{t('editEvent-images-add')}</Styled.AddLink>
					</Styled.DropzoneContainer>
				)}
			</Styled.ImagesRow>
			{error && <Styled.ErrorText>{error.message}</Styled.ErrorText>}
		</Styled.Container>
	);
};
